import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import useCompanies from '@hooks/useCompanies'
import useFilials from '@hooks/useFilials'
import { createInitFilter } from '@utils/filters'
import { Company } from '@models/company'
import {
  FilterProps,
  FilterTypes,
} from '@templates/ListPageLayout/Filters/Filter'
import { SelectProps } from '@organisms/Select'
import { FilterState } from '@hooks/useItemsFilters'

const DROPDOWN_WIDTH = 350

const getIdFromString = (value: string): number | null => {
  const parsed = Number.parseInt(value.split('/').pop() || '', 10)
  return Number.isNaN(parsed) ? null : parsed
}

const getIdByValue = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return value
  }

  if (typeof value === 'string') {
    return getIdFromString(value)
  }

  if (Array.isArray(value)) {
    return getIdByValue(value[0])
  }

  if (typeof value === 'object' && value !== null) {
    const optionValue = value as { id?: unknown; value?: unknown }

    if (typeof optionValue.id === 'number') {
      return optionValue.id
    }

    if (typeof optionValue.id === 'string') {
      return getIdFromString(optionValue.id)
    }

    if (typeof optionValue.value === 'string') {
      return getIdFromString(optionValue.value)
    }
  }

  return null
}

const parseFilterValue = (value: unknown): number[] => {
  const id = getIdByValue(value)
  return id === null ? [] : [id]
}

type ReturnType = {
  company: FilterProps<SelectProps>
  filial: FilterProps<SelectProps>
}

const useCourseAvailabilityFilters = (
  methods: UseFormReturn,
  setCourseFilters: Dispatch<SetStateAction<FilterState>>
): ReturnType => {
  const { watch, setValue } = methods

  const companiesResponse = useCompanies()
  const { items: companies = [], length: companiesLength = 0 } =
    companiesResponse.data || {}
  const companiesIsLoading = companiesResponse.isLoading

  const selectedCompany: Company | undefined = watch('availabilityCompany')
  const isInitialCompanyEffect = useRef(true)
  const [isCompanyMenuClosing, setIsCompanyMenuClosing] = useState(false)
  const [isFilialMenuClosing, setIsFilialMenuClosing] = useState(false)

  useEffect(() => {
    if (
      !companiesIsLoading &&
      companiesLength === 1 &&
      companies.length > 0 &&
      !selectedCompany
    ) {
      setValue('availabilityCompany', companies[0])
      setCourseFilters((prev) => ({
        ...prev,
        company: parseFilterValue(companies[0].value),
      }))
      setIsCompanyMenuClosing(true)
    }
  }, [companiesIsLoading, companiesLength])

  useEffect(() => {
    if (isInitialCompanyEffect.current) {
      isInitialCompanyEffect.current = false
      return
    }

    setValue('availabilityFilial', null)
    setCourseFilters((prev) => ({ ...prev, companyBranch: undefined }))
    setIsFilialMenuClosing(true)
  }, [selectedCompany?.id])

  const filialsResponse = useFilials({
    skip: !selectedCompany?.id,
    ...(selectedCompany?.id
      ? { initFilters: createInitFilter('company', selectedCompany.id) }
      : {}),
  })
  const { items: filials = [], length: filialsLength = 0 } =
    filialsResponse.data || {}
  const filialsIsLoading = filialsResponse.isLoading || filialsResponse.isFetching

  const selectedFilial = watch('availabilityFilial')

  useEffect(() => {
    if (
      !filialsIsLoading &&
      filialsLength === 1 &&
      filials.length > 0 &&
      selectedCompany &&
      !selectedFilial
    ) {
      setValue('availabilityFilial', filials[0])
      setCourseFilters((prev) => ({
        ...prev,
        companyBranch: parseFilterValue(filials[0].value),
      }))
      setIsFilialMenuClosing(true)
    }
  }, [filialsIsLoading, filialsLength, selectedCompany?.id, selectedFilial])

  useEffect(() => {
    if (!isCompanyMenuClosing) return
    setIsCompanyMenuClosing(false)
  }, [isCompanyMenuClosing])

  useEffect(() => {
    if (!isFilialMenuClosing) return
    setIsFilialMenuClosing(false)
  }, [isFilialMenuClosing])

  const company: FilterProps<SelectProps> = {
    options: companies,
    length: companiesLength,
    isLoading: companiesIsLoading,
    setPage: companiesResponse.setPage,
    setFilters: companiesResponse.setFilters,
    placeholder: 'Компания',
    fieldName: 'availabilityCompany',
    filterType: FilterTypes.SELECT_SEARCH,
    fieldNameForSubmit: 'company',
    transformValue: (filter) => ({
      company: parseFilterValue(filter.company?.[0]),
    }),
    dropdownWidth: DROPDOWN_WIDTH,
    isDisabled: false,
    // Не прячем выбранную опцию из списка. Иначе при автоподстановке (сервер вернул
    // одну подходящую компанию, её сразу выбрали) единственная опция исчезает из меню,
    // остаётся 0 элементов и рисуется ложная плашка «Нет элементов».
    hideSelectedOptions: false,
    menuIsOpen: isCompanyMenuClosing ? false : undefined,
  }

  const filial: FilterProps<SelectProps> = {
    options: filials,
    length: filialsLength,
    isLoading: filialsIsLoading,
    setPage: filialsResponse.setPage,
    setFilters: filialsResponse.setFilters,
    placeholder: 'Филиал',
    fieldName: 'availabilityFilial',
    filterType: FilterTypes.SELECT_SEARCH,
    fieldNameForSubmit: 'companyBranch',
    transformValue: (filter) => ({
      companyBranch: parseFilterValue(filter.companyBranch?.[0]),
    }),
    dropdownWidth: DROPDOWN_WIDTH,
    isDisabled: !selectedCompany,
    // См. компанию выше: та же ложная плашка при автоподстановке единственного филиала.
    hideSelectedOptions: false,
    menuIsOpen: isCompanyMenuClosing ? false : undefined,
  }

  return { company, filial }
}

export default useCourseAvailabilityFilters
